import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (mock)
    setTimeout(() => {
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        organization: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-4 py-2 mb-4">
            <span className="text-amber-600 font-medium text-sm">Get In Touch</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Let's Start Your Journey
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to transform your organization? Contact our team of experts today.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Contact Information */}
          <div>
            <div className="bg-gradient-to-br from-blue-900 to-slate-900 rounded-3xl p-8 lg:p-10 text-white h-full">
              <h3 className="text-3xl font-bold mb-8">Contact Information</h3>

              {/* Address */}
              <div className="mb-8">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-amber-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-amber-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Office Address</h4>
                    <p className="text-gray-300 leading-relaxed">
                      {companyInfo.address}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="mb-8">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-amber-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-amber-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Phone Numbers</h4>
                    {companyInfo.phone.map((phone, index) => (
                      <p key={index} className="text-gray-300 mb-1">
                        <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-amber-400 transition-colors">
                          {phone}
                        </a>
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-amber-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Email Address</h4>
                    <p className="text-gray-300">
                      <a href={`mailto:${companyInfo.email}`} className="hover:text-amber-400 transition-colors">
                        {companyInfo.email}
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <h4 className="font-bold text-lg mb-4">Office Hours</h4>
                <div className="space-y-2 text-gray-300">
                  <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p>Saturday: 9:00 AM - 2:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl border-2 border-gray-200">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+234 800 000 0000"
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3"
                  />
                </div>

                {/* Organization */}
                <div>
                  <label htmlFor="organization" className="block text-sm font-semibold text-gray-700 mb-2">
                    Organization
                  </label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Your organization name"
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help you?"
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project or inquiry..."
                    rows={5}
                    className="w-full border-2 border-gray-300 focus:border-amber-400 rounded-lg px-4 py-3 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-900 hover:from-amber-500 hover:to-yellow-700 font-semibold text-lg py-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Google Maps Section */}
        <div className="mt-12">
          <div className="bg-white rounded-3xl p-4 shadow-xl border-2 border-gray-200 overflow-hidden">
            <h3 className="text-2xl font-bold text-slate-900 mb-4 px-4 pt-4">Find Us On The Map</h3>
            <div className="w-full h-[450px] rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3939.9617108847344!2d7.486850975769243!3d9.067496890963916!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104e0ba8f3b5e5e5%3A0x1234567890abcdef!2sIndus%20Close%2C%20Wuse%20II%2C%20Abuja%2C%20Africa!5e0!3m2!1sen!2sng!4v1234567890123!5m2!1sen!2sng"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SMC Africa Office Location"
              ></iframe>
            </div>
            <div className="px-4 pb-4 pt-4">
              <p className="text-gray-600 text-center">
                <MapPin className="inline-block mr-2 text-amber-600" size={18} />
                No 3, Indus Close, Wuse II, Abuja, Off Euphrates Crescent, F.C.T-Nigeria
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
